/**
 * Reviews — Tabbed (sections/judgeme-reviews-tabs.liquid): tabs per product, and on a
 * tab with "Sub-tab products" (the Bundle tab) a second row of tabs inside its panel.
 * Each visible product view gets its cards from window.jrReviewsData, the static file
 * CI rebuilds from Judge.me (scripts/fetch-judgeme-reviews.js), keyed by product handle.
 *
 * Both tab rows are ARIA tablists: the selected tab is the only Tab stop, and the arrow
 * keys, Home and End move between the tabs of one row and select them (TASKS #167).
 * The two rows are separate: arrows never jump from one to the other.
 *
 * This was an inline script printed once per section. Handlers are delegated from the
 * document, so a section the theme editor re-renders needs only its visible views
 * filled again (shopify:section:load), and a second section on the page shares them.
 */
(function () {
  'use strict';
  if (window.JiYuReviewTabs) return;

  var SECTION = '[data-jr-section]';
  var TAB = SECTION + ' [role="tab"]';

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Locale strings ride on the section as data attributes; getAttribute returns them
     decoded, so they are escaped again on the way into the HTML below. */
  function text(sectionEl, name) {
    return sectionEl.getAttribute('data-jr-' + name) || '';
  }

  function starsSvg(sectionEl, rating) {
    var n = Math.max(0, Math.min(5, Math.round(rating || 0)));
    var html = '';
    for (var i = 1; i <= 5; i++) {
      html += '<svg viewBox="0 0 20 20" aria-hidden="true">' +
        '<path fill="' + (i <= n ? 'currentColor' : 'rgba(0,0,0,0.15)') + '" ' +
        'd="M10 1.5l2.7 5.5 6 .9-4.4 4.3 1 6-5.4-2.8-5.4 2.8 1-6L1.3 7.9l6-.9z"/>' +
        '</svg>';
    }
    // role="img" is required: aria-label is prohibited on a bare <span> (implicit
    // generic role). The role makes the star group one named image; the inner <svg>s
    // stay aria-hidden so only the label ("5 out of 5 stars") is announced.
    var label = text(sectionEl, 'stars').replace('[n]', n);
    return '<span class="jr-card__stars" role="img" aria-label="' + escapeHtml(label) + '">' + html + '</span>';
  }

  function verifiedBadge(sectionEl) {
    return '<span class="jr-card__verified">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="11" fill="#234463"/>' +
      '<path d="M7 12.5l3 3 7-7" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
      '</svg> ' + escapeHtml(text(sectionEl, 'verified')) + '</span>';
  }

  function formatDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(document.documentElement.lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) { return ''; }
  }

  function renderCard(sectionEl, r) {
    var name = escapeHtml(r.name || text(sectionEl, 'anonymous'));
    var date = escapeHtml(formatDate(r.created_at));
    var title = r.title ? '<h4 class="jr-card__title">' + escapeHtml(r.title) + '</h4>' : '';
    var body = r.body ? '<p class="jr-card__body">' + escapeHtml(r.body) + '</p>' : '';
    var photosHtml = '';
    if (r.pictures && r.pictures.length) {
      var alt = escapeHtml(text(sectionEl, 'photo'));
      photosHtml = '<div class="jr-card__photos">' + r.pictures.slice(0, 4).map(function (p) {
        if (!p.url) return '';
        return '<img class="jr-card__photo" src="' + escapeHtml(p.url) + '" width="64" height="64" loading="lazy" alt="' + alt + '">';
      }).join('') + '</div>';
    }
    return '<article class="jr-card">' +
      '<div class="jr-card__head">' + starsSvg(sectionEl, r.rating) + title + '</div>' +
      body +
      photosHtml +
      '<div class="jr-card__meta">' +
        '<span class="jr-card__name">' + name + '</span>' +
        (date ? '<span>' + date + '</span>' : '') +
        (r.verified ? verifiedBadge(sectionEl) : '') +
      '</div>' +
    '</article>';
  }

  function renderView(sectionEl, view) {
    var listEl = view.querySelector('[data-jr-list]');
    if (!listEl || listEl.dataset.jrRendered === 'true') return;
    var data = (window.jrReviewsData || {})[view.getAttribute('data-jr-product-handle')];
    if (!data || data.error) {
      // No data yet for this product: the data file is rebuilt daily from the products
      // the themes' templates name, so one picked since the last run has none. Shoppers
      // keep the rating and the link to all reviews; the merchant gets told why.
      if (window.Shopify && window.Shopify.designMode) {
        listEl.innerHTML = '<div class="jr-list__error">No review data for this product yet. ' +
          'It is added on the next daily refresh (GitHub Actions: Refresh Judge.me reviews).</div>';
      } else {
        listEl.hidden = true;
      }
    } else if (!data.reviews || data.reviews.length === 0) {
      listEl.innerHTML = '<div class="jr-list__empty">' + escapeHtml(text(sectionEl, 'empty')) + '</div>';
    } else {
      listEl.innerHTML = data.reviews.map(function (r) { return renderCard(sectionEl, r); }).join('');
    }
    listEl.dataset.jrRendered = 'true';
  }

  /* Fill every view whose panel, and sub-panel if it has one, is selected. */
  function renderVisible(sectionEl) {
    sectionEl.querySelectorAll('[data-jr-view]').forEach(function (view) {
      if (!view.closest('[role="tabpanel"]:not(.is-active)')) renderView(sectionEl, view);
    });
  }

  function tabsOf(tab) {
    return Array.prototype.filter.call(tab.parentElement.children, function (el) {
      return el.getAttribute('role') === 'tab';
    });
  }

  function select(tab, moveFocus) {
    tabsOf(tab).forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) t.removeAttribute('tabindex');
      else t.setAttribute('tabindex', '-1');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.classList.toggle('is-active', on);
    });
    var sectionEl = tab.closest(SECTION);
    if (sectionEl) renderVisible(sectionEl);
    if (moveFocus) tab.focus();
  }

  /* Select a tab and, for a sub-tab, the tab whose panel holds it first. */
  function reveal(tab) {
    var panel = tab.parentElement.closest('[role="tabpanel"]');
    var owner = panel && document.getElementById(panel.getAttribute('aria-labelledby'));
    if (owner) reveal(owner);
    if (tab.getAttribute('aria-selected') !== 'true') select(tab, false);
  }

  document.addEventListener('click', function (e) {
    var tab = e.target.closest(TAB);
    if (tab) select(tab, false);
  });

  /* Arrow keys, Home and End move between the tabs of one row and select them (a
     horizontal tablist, wrapping at either end). */
  document.addEventListener('keydown', function (e) {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    var tab = e.target.closest && e.target.closest(TAB);
    if (!tab) return;
    var tabs = tabsOf(tab);
    var i = tabs.indexOf(tab);
    var next = null;
    if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;
    e.preventDefault();
    select(next, true);
  });

  function init() {
    document.querySelectorAll(SECTION).forEach(renderVisible);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (e) {
    var sectionEl = e.target && e.target.querySelector && e.target.querySelector(SECTION);
    if (sectionEl) renderVisible(sectionEl);
  });

  /* Theme editor: selecting a Review tab block shows its tab. */
  document.addEventListener('shopify:block:select', function (e) {
    var tab = document.querySelector(SECTION + ' [role="tab"][data-jr-tab="' + e.detail.blockId + '"]');
    if (tab) reveal(tab);
  });

  // Sync to the homepage product switcher: when the customer changes products there,
  // show the review tab (or bundle sub-tab) for the product they're looking at.
  //
  // v3 bundle handling: if the switcher's "Bundle & Save" tab is active, the customer
  // is on one of N configured bundles. A sub-tab for that bundle wins; failing that,
  // any tab or sub-tab for another of the switcher's bundles does, so the Bundle tab
  // still opens for a bundle it has no button for.
  document.addEventListener('jiyu:hps:product-changed', function (e) {
    var handle = e.detail && e.detail.handle;
    if (!handle) return;

    var handlesToTry = [handle];
    var onBundle = !!document.querySelector(
      '[data-homepage-product-switcher] [data-pfs-bundles-tab].pfs__option--active'
    );
    if (onBundle) {
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-homepage-product-switcher] .pfs__bundle-card[data-handle]'),
        function (c) {
          var h = c.getAttribute('data-handle');
          if (h && h !== handle) handlesToTry.push(h);
        }
      );
    }

    document.querySelectorAll(SECTION).forEach(function (sectionEl) {
      for (var i = 0; i < handlesToTry.length; i++) {
        var match = sectionEl.querySelector('[role="tab"][data-jr-product-handle="' + handlesToTry[i] + '"]');
        if (match) {
          reveal(match);
          break;
        }
      }
    });
  });

  window.JiYuReviewTabs = { renderVisible: renderVisible, reveal: reveal };
})();
